import React, { useState, useEffect } from 'react';
import { X, Fingerprint, CheckCircle, AlertCircle } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import fingerprintApi, { FingerprintDevice } from '../services/fingerprintApi';
import { useWebSocket } from '../contexts/WebSocketContext';
import toast from 'react-hot-toast';

interface Employee {
  id: number;
  user: {
    name: string;
    email: string;
  };
}

interface FingerprintVerificationModalProps {
  employee: Employee;
  onClose: () => void;
  onVerifySuccess?: (employee: Employee) => void;
}

const FingerprintVerificationModal: React.FC<FingerprintVerificationModalProps> = ({
  employee,
  onClose,
  onVerifySuccess
}) => {
  const [step, setStep] = useState<'connect' | 'verifying' | 'success' | 'error'>('connect');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [savedTemplate, setSavedTemplate] = useState<string>('');
  const { isConnected, fingerprintSocketService } = useWebSocket();

  // Get available devices for current admin's tenant
  const { data: devicesData } = useQuery({
    queryKey: ['fingerprint-devices-my'],
    queryFn: () => fingerprintApi.getMyFingerprintDevices(),
    enabled: true
  });

  const devices: FingerprintDevice[] = devicesData?.devices || [];

  // Auto-select first device when devices are loaded
  useEffect(() => {
    if (devices.length > 0 && !selectedDeviceId) {
      const firstActiveDevice = devices.find(d => d.status === 'active') || devices[0];
      if (firstActiveDevice) {
        setSelectedDeviceId(firstActiveDevice.deviceId);
      }
    }
  }, [devices, selectedDeviceId]);

  // Load saved template from database
  const { data: fingerprintData, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ['fingerprint-template', employee.id],
    queryFn: () => fingerprintApi.getFingerprintTemplate(employee.id),
    enabled: !!employee.id
  });

  // Update saved template when data is fetched
  useEffect(() => {
    if (fingerprintData?.employee?.fingerprintTemplate) {
      setSavedTemplate(fingerprintData.employee.fingerprintTemplate);
    } else {
      setSavedTemplate('');
    }
  }, [fingerprintData]);

  // Listen for status updates from device
  useEffect(() => {
    if (!selectedDeviceId || !fingerprintSocketService) return;

    const cleanup = fingerprintSocketService.setupStatusListener(
      selectedDeviceId,
      (status: string) => {
        console.log('[VERIFICATION MODAL] Status update:', status);
        setStatusMessage(status);
      }
    );

    return cleanup;
  }, [selectedDeviceId, fingerprintSocketService]);

  const verifyMutation = useMutation({
    mutationFn: ({ deviceId, template }: { deviceId: number; template: string }) =>
      fingerprintApi.verifyFingerprint({
        deviceId,
        fingerprintTemplate: template
      }),
    onSuccess: (response) => {
      setStep('success');
      setStatusMessage('Fingerprint verified successfully!');
      toast.success('Fingerprint verified successfully');
      if (onVerifySuccess) {
        onVerifySuccess(employee);
      }
    },
    onError: (error: any) => {
      setStep('error');
      setErrorMessage(error.response?.data?.message || 'Fingerprint verification failed');
      setStatusMessage('');
      toast.error(error.response?.data?.message || 'Fingerprint verification failed');
    }
  });

  const handleVerify = async () => {
    if (!selectedDeviceId) {
      toast.error('Please select a device first');
      return;
    }

    if (!savedTemplate) {
      toast.error('No fingerprint template found for this employee');
      return;
    }

    if (!isConnected) {
      toast.error('WebSocket not connected. Please wait...');
      return;
    }

    try {
      setErrorMessage('');
      setStatusMessage('Starting verification...');
      setStep('verifying');

      console.log('[VERIFICATION MODAL] Starting verification for employee ID:', employee.id, 'device:', selectedDeviceId);
      
      verifyMutation.mutate({
        deviceId: selectedDeviceId,
        template: savedTemplate
      });
    } catch (error: any) {
      console.error('[VERIFICATION MODAL] Error in handleVerify:', error);
      setStep('error');
      setErrorMessage(error.message || 'Failed to verify fingerprint');
      setStatusMessage('');
    }
  };

  const handleClose = () => {
    setStatusMessage('');
    setErrorMessage('');
    onClose();
  };

  if (isLoadingTemplate) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading fingerprint template...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!savedTemplate) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Verify Fingerprint</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="text-center py-8">
            <AlertCircle className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Fingerprint Found</h4>
            <p className="text-sm text-gray-600 mb-6">
              This employee does not have a fingerprint enrolled. Please enroll a fingerprint first.
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Verify Fingerprint</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Employee: <span className="font-medium">{employee.user.name}</span>
          </p>
        </div>

        {step === 'connect' && (
          <div className="text-center py-8">
            <Fingerprint className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Select Device</h4>
            
            <div className="mb-6 text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fingerprint Device:
              </label>
              <select
                value={selectedDeviceId || ''}
                onChange={(e) => setSelectedDeviceId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">-- Select Device --</option>
                {devices.map((device) => (
                  <option key={device.id} value={device.deviceId}>
                    {device.name} (ID: {device.deviceId}) {device.status === 'active' ? '🟢' : '🔴'}
                  </option>
                ))}
              </select>
              {devices.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">No devices available. Please contact administrator.</p>
              )}
              {!isConnected && (
                <p className="mt-2 text-xs text-yellow-600">WebSocket not connected. Please wait...</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={handleVerify}
                disabled={!selectedDeviceId || !isConnected || verifyMutation.isPending}
                className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifyMutation.isPending ? 'Verifying...' : 'Start Verification'}
              </button>
            </div>
          </div>
        )}

        {step === 'verifying' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">Verifying Fingerprint</h4>
            <p className="text-sm text-gray-600 mb-2">
              Please place your finger on the sensor and wait...
            </p>
            {statusMessage && (
              <p className="text-sm text-primary-600 font-medium mt-2">
                {statusMessage}
              </p>
            )}
          </div>
        )}

        {step === 'success' && (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Verification Successful</h4>
            <p className="text-sm text-gray-600 mb-6">
              Fingerprint has been verified successfully.
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Close
            </button>
          </div>
        )}

        {step === 'error' && (
          <div className="text-center py-8">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Verification Failed</h4>
            <p className="text-sm text-red-600 mb-6">{errorMessage}</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setStep('connect');
                  setErrorMessage('');
                }}
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Try Again
              </button>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FingerprintVerificationModal;

